import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Button, 
  Chip, 
  Divider, 
  CircularProgress, 
  Card, 
  CardContent, 
  Alert, 
  Tabs, 
  Tab, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Tooltip
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { Add as AddIcon, Delete as DeleteIcon, Terminal as TerminalIcon } from '@mui/icons-material';
import apiService from '../services/api';
import { formatMemory, formatCpuTime } from '../utils/formatters';
import PortForwardingSection from '../components/PortForwardingSection';
import VMConsoleViewer from '../components/VMConsoleViewer';

// VM 상태에 따른 색상 지정
const getStatusColor = (state) => {
  const stateMap = {
    1: 'success', // 실행 중
    3: 'warning', // 일시 중지 중
    5: 'error',   // 종료 중
    6: 'error',   // 크래시됨
  };
  return stateMap[state] || 'default';
};

// VM 상태 번호를 문자열로 변환
const getStatusText = (state) => {
  const stateMap = {
    0: '종료됨',
    1: '실행 중',
    2: '일시 중지됨',
    3: '일시 중지 중',
    4: '종료 중',
    5: '크래시됨',
    6: 'PM 일시 중지됨'
  };
  return stateMap[state] || '알 수 없음';
};

const VMDetailPage = () => {
  const { vmName } = useParams();
  const navigate = useNavigate();
  const [vmInfo, setVmInfo] = useState(null);
  const [vmStatus, setVmStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [removeStorage, setRemoveStorage] = useState(true);
  const [cloudInitIso, setCloudInitIso] = useState(null);
  const [removeCloudInitIso, setRemoveCloudInitIso] = useState(false);
  const [isoInUseInfo, setIsoInUseInfo] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [volumes, setVolumes] = useState([]);
  const [volumesLoading, setVolumesLoading] = useState(false);
  const [mountDialogOpen, setMountDialogOpen] = useState(false);
  const [mountRequest, setMountRequest] = useState({
    vm_name: vmName,
    volume_name: '',
    target_dev: '',
    bus_type: 'virtio',
    read_only: false
  });
  const [availableVolumes, setAvailableVolumes] = useState([]);
  
  // 네트워크 관리 상태
  const [vmNetworks, setVmNetworks] = useState([]);
  const [availableNetworks, setAvailableNetworks] = useState([]);
  const [networksLoading, setNetworksLoading] = useState(false);
  const [networkDialogOpen, setNetworkDialogOpen] = useState(false);
  const [networkRestartDialogOpen, setNetworkRestartDialogOpen] = useState(false);
  const [networkRequest, setNetworkRequest] = useState({
    network_profile: '',
    ip_address: ''
  });
  
  // Cloud-init 관리 상태
  const [availableCloudInitIsos, setAvailableCloudInitIsos] = useState([]);
  const [isoUsageInfo, setIsoUsageInfo] = useState({});
  const [cloudInitLoading, setCloudInitLoading] = useState(false);
  const [cloudInitDialogOpen, setCloudInitDialogOpen] = useState(false);
  const [selectedCloudInitIso, setSelectedCloudInitIso] = useState('');
  
  // VM 정보와 상태 로드
  const loadVMInfo = async () => {
    setLoading(true);
    try {
      const [infoResponse, statusResponse, cloudInitResponse] = await Promise.all([
        apiService.getVMInfo(vmName),
        apiService.getVMStatus(vmName),
        apiService.getVMCloudInitIso(vmName)
      ]);
      
      setVmInfo(infoResponse.data);
      setVmStatus(statusResponse.data);
      
      // cloud-init ISO 정보 설정
      const isoInfo = cloudInitResponse.data.iso_info;
      setCloudInitIso(isoInfo);
      
      // ISO가 있으면 다른 VM에서 사용 중인지 확인
      if (isoInfo) {
        try {
          const inUseResponse = await apiService.checkCloudInitIsoInUse(isoInfo.name);
          setIsoInUseInfo(inUseResponse.data);
        } catch (err) {
        }
      }
      
      setError(null);
    } catch (err) {
      
      setError('VM 정보를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };
  
  
  // 네트워크 목록 로드
  const loadNetworks = async () => {
    const networkManagementTabIndex = vmInfo?.gpu ? 3 : 2;

    if (activeTab === networkManagementTabIndex) {
      setNetworksLoading(true);
      try {
        const [vmNetworksResponse, availableNetworksResponse] = await Promise.all([
          apiService.getVMNetworks(vmName),
          apiService.getNetworks()
        ]);
        setVmNetworks(vmNetworksResponse.data.interfaces || []);
        setAvailableNetworks(availableNetworksResponse.data.networks || []);
      } catch (err) {
        
        setNotification({
          open: true,
          message: '네트워크 정보를 불러오는데 실패했습니다.',
          severity: 'error'
        });
      } finally {
        setNetworksLoading(false);
      }
    }
  };

  // Cloud-init ISO 목록 로드
  const loadCloudInitIsos = async () => {
    // Cloud-init 관리 탭인지 확인
    const cloudInitTabIndex = vmInfo?.gpu ? 5 : 4;

    if (activeTab === cloudInitTabIndex) {
      setCloudInitLoading(true);
      try {
        const response = await apiService.getCloudInitIsos();
        const isos = response.data.isos || [];
        setAvailableCloudInitIsos(isos);

        // 각 ISO의 사용 정보 확인
        const usageInfo = {};
        for (const iso of isos) {
          try {
            const usageResponse = await apiService.checkCloudInitIsoInUse(iso.name);
            usageInfo[iso.name] = usageResponse.data;
          } catch (err) {
            usageInfo[iso.name] = { in_use: false, using_vms: [] };
          }
        }
        setIsoUsageInfo(usageInfo);
      } catch (err) {
        
        setNotification({
          open: true,
          message: 'Cloud-init ISO 목록을 불러오는데 실패했습니다.',
          severity: 'error'
        });
      } finally {
        setCloudInitLoading(false);
      }
    }
  };

  // 디스크 볼륨 목록 로드
  const loadVolumes = async () => {
    // 디스크 관리 탭인지 확인 (GPU가 있으면 인덱스 4, 없으면 인덱스 3)
    const diskManagementTabIndex = vmInfo?.gpu ? 4 : 3;

    if (activeTab === diskManagementTabIndex) {
      setVolumesLoading(true);
      try {
        // 사용 가능한 볼륨 목록 로드
        const response = await apiService.getVolumes();
        setAvailableVolumes(response.data.volumes);
        
        // VM XML에서 디스크 정보 파싱
        if (vmInfo && vmInfo.xml) {
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(vmInfo.xml, "text/xml");
          
          // 디스크 요소 추출
          const diskElements = xmlDoc.getElementsByTagName("disk");
          const parsedDisks = [];
          
          for (let i = 0; i < diskElements.length; i++) {
            const disk = diskElements[i];
            
            // 디스크 타입이 'disk'인 것만 처리 (CD-ROM 등 제외)
            if (disk.getAttribute("device") === "disk") {
              // 소스 파일 경로
              const sourceElement = disk.getElementsByTagName("source")[0];
              if (!sourceElement) continue;
              
              const filePath = sourceElement.getAttribute("file");
              if (!filePath) continue;
              
              // 볼륨 이름 추출 (경로에서 파일명만)
              const volumeName = filePath.split('/').pop();
              
              // 타겟 장치 정보
              const targetElement = disk.getElementsByTagName("target")[0];
              if (!targetElement) continue;
              
              const targetDev = targetElement.getAttribute("dev");
              const busType = targetElement.getAttribute("bus");
              
              // 드라이버 정보 (디스크 형식)
              const driverElement = disk.getElementsByTagName("driver")[0];
              let diskFormat = "unknown";
              if (driverElement) {
                diskFormat = driverElement.getAttribute("type") || "unknown";
              }
              
              // 읽기 전용 여부
              const readOnly = disk.getElementsByTagName("readonly").length > 0;
              
              parsedDisks.push({
                target_dev: targetDev,
                volume_name: volumeName,
                bus_type: busType || "virtio",
                disk_format: diskFormat,
                read_only: readOnly,
                file_path: filePath,
                isRoot: targetDev === 'vda' // vda는 일반적으로 루트 디스크
              });
            }
          }
          
          setVolumes(parsedDisks);
        }
      } catch (err) {
        
      } finally {
        setVolumesLoading(false);
      }
    }
  };
  
  // 초기 데이터 로드
  useEffect(() => {
    loadVMInfo();
  }, [vmName]);
  
  
  // 활성 탭에 따른 데이터 로드
  useEffect(() => {
    if (activeTab === (vmInfo?.gpu ? 3 : 2)) {
      // 네트워크 관리 탭
      loadNetworks();
    } else if (activeTab === (vmInfo?.gpu ? 4 : 3)) {
      // 디스크 관리 탭
      loadVolumes();
    } else if (activeTab === (vmInfo?.gpu ? 5 : 4)) {
      // Cloud-init 관리 탭
      loadCloudInitIsos();
    }
  }, [activeTab, vmInfo]);
  
  // VM 시작
  const handleStart = async () => {
    try {
      await apiService.startVM(vmName);
      setNotification({ open: true, message: 'VM 시작 요청이 전송되었습니다.', severity: 'success' });
      
      // 상태 갱신을 위해 잠시 후 정보 다시 로드
      setTimeout(() => {
        loadVMInfo();
      }, 2000);
    } catch (err) {
      setNotification({ open: true, message: `VM 시작 실패: ${err.response?.data?.detail || err.message}`, severity: 'error' });
    }
  };
  
  // VM 종료
  const handleShutdown = async () => {
    try {
      await apiService.shutdownVM(vmName);
      setNotification({ open: true, message: 'VM 종료 요청이 전송되었습니다.', severity: 'success' });
      
      // 상태 갱신을 위해 잠시 후 정보 다시 로드
      setTimeout(() => {
        loadVMInfo();
      }, 2000);
    } catch (err) {
      setNotification({ open: true, message: `VM 종료 실패: ${err.response?.data?.detail || err.message}`, severity: 'error' });
    }
  };
  
  // VM 재시작
  const handleReset = async () => {
    try {
      await apiService.resetVM(vmName);
      setNotification({ open: true, message: 'VM 재시작 요청이 전송되었습니다.', severity: 'success' });
      
      // 상태 갱신을 위해 잠시 후 정보 다시 로드
      setTimeout(() => {
        loadVMInfo();
      }, 2000);
    } catch (err) {
      setNotification({ open: true, message: `VM 재시작 실패: ${err.response?.data?.detail || err.message}`, severity: 'error' });
    }
  };
  
  // VM 삭제
  const handleDelete = async () => {
    try {
      // VM 삭제
      await apiService.deleteVM(vmName, removeStorage);
      
      // cloud-init ISO 삭제 (선택한 경우)
      if (cloudInitIso && removeCloudInitIso) {
        try {
          // 다른 VM에서 사용 중이지 않거나 강제 삭제 선택 시 삭제
          const shouldForceDelete = isoInUseInfo && isoInUseInfo.in_use && 
                                    isoInUseInfo.using_vms.length > 1;
          
          await apiService.deleteCloudInitIso(cloudInitIso.name, shouldForceDelete);
          
        } catch (err) {
          
          // VM은 이미 삭제되었으므로 계속 진행
        }
      }
      
      setNotification({ open: true, message: 'VM이 삭제되었습니다.', severity: 'success' });
      // 목록 페이지로 이동
      navigate('/vms');
    } catch (err) {
      setNotification({ open: true, message: `VM 삭제 실패: ${err.response?.data?.detail || err.message}`, severity: 'error' });
    } finally {
      setDeleteDialogOpen(false);
    }
  };
  
  // 알림 닫기
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // 네트워크 할당 (VM 상태 확인 후 처리)
  const handleAssignNetwork = async () => {
    // VM이 실행 중인지 확인
    if (vmStatus?.active) {
      // 재시작 확인 다이얼로그 표시
      setNetworkRestartDialogOpen(true);
    } else {
      // VM이 중지된 상태면 바로 진행
      await performNetworkAssignment();
    }
  };

  // 실제 네트워크 할당 수행
  const performNetworkAssignment = async () => {
    let wasRunning = false;
    let networkAssigned = false;
    
    try {
      setNetworksLoading(true);
      
      // VM이 실행 중이면 먼저 중지
      wasRunning = vmStatus?.active;
      if (wasRunning) {
        setNotification({
          open: true,
          message: 'VM을 중지하는 중...',
          severity: 'info'
        });
        
        try {
          await apiService.shutdownVM(vmName);
          
          // VM 완전 중지 대기 (최대 30초)
          let vmStopped = false;
          for (let i = 0; i < 30; i++) {
            await new Promise(resolve => setTimeout(resolve, 1000));
            const statusResponse = await apiService.getVMStatus(vmName);
            if (!statusResponse.data.active) {
              vmStopped = true;
              break;
            }
          }
          
          if (!vmStopped) {
            throw new Error('VM 중지 시간 초과 (30초)');
          }
        } catch (shutdownError) {
          throw new Error(`VM 중지 실패: ${shutdownError.message}`);
        }
      }
      
      // 네트워크 할당
      try {
        await apiService.assignVMToNetwork(vmName, networkRequest);
        networkAssigned = true;
        
        setNotification({
          open: true,
          message: '네트워크 할당 완료',
          severity: 'success'
        });
      } catch (networkError) {
        throw new Error(`네트워크 할당 실패: ${networkError.response?.data?.detail || networkError.message}`);
      }
      
      // VM이 실행 중이었으면 다시 시작
      if (wasRunning) {
        setNotification({
          open: true,
          message: '네트워크 변경 완료. VM을 시작하는 중...',
          severity: 'info'
        });
        
        try {
          await apiService.startVM(vmName);
          
          setNotification({
            open: true,
            message: 'VM 재시작 완료. 네트워크가 성공적으로 변경되었습니다.',
            severity: 'success'
          });
        } catch (startError) {
          setNotification({
            open: true,
            message: `네트워크는 변경되었지만 VM 시작 실패: ${startError.message}. 수동으로 시작해주세요.`,
            severity: 'warning'
          });
        }
      } else {
        setNotification({
          open: true,
          message: '네트워크가 성공적으로 할당되었습니다.',
          severity: 'success'
        });
      }
      
      setNetworkDialogOpen(false);
      setNetworkRestartDialogOpen(false);
      setNetworkRequest({ network_profile: '', ip_address: '' });
      
      // VM 정보 다시 로드
      await loadVMInfo();
      loadNetworks();
      
    } catch (err) {
      // 오류 발생 시 롤백 시도
      if (wasRunning && !networkAssigned) {
        // 네트워크 할당 전에 실패한 경우 VM 다시 시작 시도
        try {
          setNotification({
            open: true,
            message: '오류 발생. VM을 다시 시작하는 중...',
            severity: 'info'
          });
          await apiService.startVM(vmName);
          
          setNotification({
            open: true,
            message: `작업 실패했지만 VM은 복구되었습니다: ${err.message}`,
            severity: 'error'
          });
        } catch (rollbackError) {
          setNotification({
            open: true,
            message: `작업 실패 및 VM 복구 실패: ${err.message}. VM을 수동으로 시작해주세요.`,
            severity: 'error'
          });
        }
      } else {
        setNotification({
          open: true,
          message: err.message || '네트워크 변경 중 오류가 발생했습니다.',
          severity: 'error'
        });
      }
      
      // VM 정보 다시 로드하여 현재 상태 확인
      await loadVMInfo();
    } finally {
      setNetworksLoading(false);
    }
  };

  // 네트워크 해제
  const handleUnassignNetwork = async (networkName) => {
    if (!window.confirm(`네트워크 '${networkName}'을 정말 해제하시겠습니까?`)) {
      return;
    }

    let wasRunning = false;
    let networkUnassigned = false;
    
    try {
      setNetworksLoading(true);
      
      // VM이 실행 중이면 먼저 중지
      wasRunning = vmStatus?.active;
      if (wasRunning) {
        setNotification({
          open: true,
          message: 'VM을 중지하는 중...',
          severity: 'info'
        });
        
        try {
          await apiService.shutdownVM(vmName);
          
          // VM 완전 중지 대기 (최대 30초)
          let vmStopped = false;
          for (let i = 0; i < 30; i++) {
            await new Promise(resolve => setTimeout(resolve, 1000));
            const statusResponse = await apiService.getVMStatus(vmName);
            if (!statusResponse.data.active) {
              vmStopped = true;
              break;
            }
          }
          
          if (!vmStopped) {
            throw new Error('VM 중지 시간 초과 (30초)');
          }
        } catch (shutdownError) {
          throw new Error(`VM 중지 실패: ${shutdownError.message}`);
        }
      }
      
      // 네트워크 해제
      try {
        await apiService.unassignVMFromNetwork(vmName, networkName);
        networkUnassigned = true;
        
        setNotification({
          open: true,
          message: '네트워크 해제 완료',
          severity: 'success'
        });
      } catch (networkError) {
        throw new Error(`네트워크 해제 실패: ${networkError.response?.data?.detail || networkError.message}`);
      }
      
      // VM이 실행 중이었으면 다시 시작
      if (wasRunning) {
        setNotification({
          open: true,
          message: '네트워크 해제 완료. VM을 시작하는 중...',
          severity: 'info'
        });
        
        try {
          await apiService.startVM(vmName);
          
          setNotification({
            open: true,
            message: 'VM 재시작 완료. 네트워크가 성공적으로 해제되었습니다.',
            severity: 'success'
          });
        } catch (startError) {
          setNotification({
            open: true,
            message: `네트워크는 해제되었지만 VM 시작 실패: ${startError.message}. 수동으로 시작해주세요.`,
            severity: 'warning'
          });
        }
      } else {
        setNotification({
          open: true,
          message: '네트워크가 성공적으로 해제되었습니다.',
          severity: 'success'
        });
      }
      
      // VM 정보 다시 로드
      await loadVMInfo();
      loadNetworks();
      
    } catch (err) {
      // 오류 발생 시 롤백 시도
      if (wasRunning && !networkUnassigned) {
        // 네트워크 해제 전에 실패한 경우 VM 다시 시작 시도
        try {
          setNotification({
            open: true,
            message: '오류 발생. VM을 다시 시작하는 중...',
            severity: 'info'
          });
          await apiService.startVM(vmName);
          
          setNotification({
            open: true,
            message: `작업 실패했지만 VM은 복구되었습니다: ${err.message}`,
            severity: 'error'
          });
        } catch (rollbackError) {
          setNotification({
            open: true,
            message: `작업 실패 및 VM 복구 실패: ${err.message}. VM을 수동으로 시작해주세요.`,
            severity: 'error'
          });
        }
      } else {
        setNotification({
          open: true,
          message: err.message || '네트워크 해제 중 오류가 발생했습니다.',
          severity: 'error'
        });
      }
      
      // VM 정보 다시 로드하여 현재 상태 확인
      await loadVMInfo();
    } finally {
      setNetworksLoading(false);
    }
  };

  // 네트워크 할당 다이얼로그 열기
  const handleNetworkDialogOpen = () => {
    // 첫 번째 사용 가능한 네트워크로 기본값 설정
    if (availableNetworks.length > 0) {
      setNetworkRequest({
        network_profile: availableNetworks[0].name,
        ip_address: ''
      });
    }
    setNetworkDialogOpen(true);
  };
  
  // 디스크 마운트 다이얼로그 열기
  const handleMountDialogOpen = async () => {
    // 사용 가능한 볼륨 목록이 없다면 로드
    if (availableVolumes.length === 0) {
      try {
        
        const response = await apiService.getVolumes();
        
        
        
        
        setAvailableVolumes(response.data.volumes);
        
        setMountRequest({
          vm_name: vmName,
          volume_name: response.data.volumes.length > 0 ? response.data.volumes[0].name : '',
          target_dev: '',
          bus_type: 'virtio',
          read_only: false
        });
      } catch (err) {
        
        
        setNotification({
          open: true,
          message: '볼륨 목록을 불러오는데 실패했습니다.',
          severity: 'error'
        });
        return;
      }
    } else {
      
      setMountRequest({
        vm_name: vmName,
        volume_name: availableVolumes.length > 0 ? availableVolumes[0].name : '',
        target_dev: '',
        bus_type: 'virtio',
        read_only: false
      });
    }
    
    setMountDialogOpen(true);
  };
  
  // 디스크 마운트 다이얼로그 닫기
  const handleMountDialogClose = () => {
    setMountDialogOpen(false);
  };
  
  // 디스크 마운트 요청 처리
  const handleMountDisk = async () => {
    try {
      const response = await apiService.mountDiskToVM(vmName, mountRequest);
      setNotification({
        open: true,
        message: `디스크 볼륨이 VM에 성공적으로 마운트되었습니다.`,
        severity: 'success'
      });
      // VM 정보를 다시 로드하여 XML에서 새로 마운트된 디스크 정보를 가져옴
      await loadVMInfo();
      loadVolumes();
      handleMountDialogClose();
    } catch (err) {
      
      setNotification({
        open: true,
        message: `디스크 마운트 실패: ${err.response?.data?.detail || err.message}`,
        severity: 'error'
      });
    }
  };
  
  // 디스크 언마운트 처리
  const handleUnmountDisk = async (targetDev) => {
    try {
      await apiService.unmountDiskFromVM(vmName, targetDev);
      setNotification({
        open: true,
        message: `디스크가 VM에서 성공적으로 언마운트되었습니다.`,
        severity: 'success'
      });
      // VM 정보를 다시 로드하여 XML에서 업데이트된 디스크 정보를 가져옴
      await loadVMInfo();
      loadVolumes();
    } catch (err) {
      
      setNotification({
        open: true,
        message: `디스크 언마운트 실패: ${err.response?.data?.detail || err.message}`,
        severity: 'error'
      });
    }
  };
  
  // 입력 필드 변경 처리
  const handleMountInputChange = (e) => {
    const { name, value, checked } = e.target;
    setMountRequest(prev => ({
      ...prev,
      [name]: name === 'read_only' ? checked : value
    }));
  };

  // Cloud-init ISO 변경 다이얼로그 열기
  const handleCloudInitDialogOpen = () => {
    // 현재 ISO를 기본값으로 설정
    setSelectedCloudInitIso(cloudInitIso?.name || '');
    setCloudInitDialogOpen(true);
  };

  // Cloud-init ISO 변경 처리
  const handleUpdateCloudInitIso = async () => {
    try {
      setCloudInitLoading(true);

      // VM이 실행 중인지 확인
      if (vmStatus?.active) {
        setNotification({
          open: true,
          message: 'VM이 실행 중입니다. Cloud-init ISO를 변경하려면 먼저 VM을 중지하세요.',
          severity: 'warning'
        });
        return;
      }

      await apiService.updateVMCloudInitIso(vmName, selectedCloudInitIso);
      
      setNotification({
        open: true,
        message: selectedCloudInitIso 
          ? `Cloud-init ISO가 '${selectedCloudInitIso}'로 변경되었습니다.`
          : 'Cloud-init ISO가 제거되었습니다.',
        severity: 'success'
      });

      // VM 정보 다시 로드
      await loadVMInfo();
      setCloudInitDialogOpen(false);

    } catch (err) {
      
      setNotification({
        open: true,
        message: `Cloud-init ISO 변경 실패: ${err.response?.data?.detail || err.message}`,
        severity: 'error'
      });
    } finally {
      setCloudInitLoading(false);
    }
  };
  
  // 탭 변경 처리
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ mt: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" gutterBottom>
          VM 상세 정보
        </Typography>
        
        <Box>
          {vmStatus && (
            <>
              {!Boolean(vmStatus.active) && (
                <Button 
                  variant="contained" 
                  color="primary" 
                  sx={{ mr: 1 }}
                  onClick={handleStart}
                >
                  시작
                </Button>
              )}
              
              {Boolean(vmStatus.active) && (
                <>
                  <Button 
                    variant="contained" 
                    color="info" 
                    startIcon={<TerminalIcon />}
                    sx={{ mr: 1 }}
                    onClick={() => navigate(`/terminal/${vmName}`)}
                  >
                    터미널
                  </Button>
                  <Button 
                    variant="contained" 
                    color="warning" 
                    sx={{ mr: 1 }}
                    onClick={handleReset}
                  >
                    재시작
                  </Button>
                  <Button 
                    variant="contained" 
                    color="error" 
                    sx={{ mr: 1 }}
                    onClick={handleShutdown}
                  >
                    종료
                  </Button>
                </>
              )}
              
              <Button 
                variant="outlined" 
                color="error"
                onClick={() => setDeleteDialogOpen(true)}
              >
                삭제
              </Button>
            </>
          )}
        </Box>
      </Box>
      
      {/* 상태 정보 */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Typography variant="h5" component="span">
              {vmName}
            </Typography>
          </Grid>
          <Grid item>
            {vmStatus && (
              <Chip 
                label={getStatusText(vmStatus.state)} 
                color={getStatusColor(vmStatus.state)} 
                variant="outlined" 
              />
            )}
          </Grid>
        </Grid>
      </Paper>
      
      {/* 탭 */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
        >
          <Tab label="기본 정보" />
          <Tab label="콘솔" />
          {vmInfo?.gpu && <Tab label="GPU 정보" />}
          <Tab label="네트워크 관리" />
          <Tab label="디스크 관리" />
          <Tab label="Cloud-init 관리" />
        </Tabs>
        
        <Box sx={{ p: 3 }}>
          {/* 기본 정보 탭 */}
          {activeTab === 0 && vmInfo && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      하드웨어 정보
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2">UUID</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {vmInfo.uuid}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2">vCPU</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {vmInfo.vcpus}개
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2">메모리</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatMemory(vmInfo.memory)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2">자동 시작</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {vmInfo.autostart ? "활성화" : "비활성화"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      상태 정보
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2">현재 상태</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {getStatusText(vmInfo.state)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2">실행 중</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {Boolean(vmStatus?.active) ? "예" : "아니오"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2">영구적</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {Boolean(vmStatus?.persistent) ? "예" : "아니오"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2">CPU 시간</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatCpuTime(vmInfo.cpu_time)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* 콘솔 탭 */}
          {activeTab === 1 && (
            <Box>
              <VMConsoleViewer vmName={vmName} />
              {!vmStatus?.active && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  VM이 실행 중이 아닙니다. 실시간 콘솔 출력을 보려면 VM을 시작해주세요.
                </Alert>
              )}
            </Box>
          )}

          {/* GPU 정보 탭 */}
          {activeTab === 2 && vmInfo?.gpu && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  할당된 GPU 정보
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2">GPU 모델</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {vmInfo.gpu.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2">PCI ID</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {vmInfo.gpu.pci_id}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
          
          {/* 네트워크 관리 탭 */}
          {activeTab === (vmInfo?.gpu ? 3 : 2) && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">
                  네트워크 관리
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleNetworkDialogOpen}
                  disabled={availableNetworks.length === 0}
                >
                  네트워크 할당
                </Button>
              </Box>

              {networksLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : vmNetworks.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    할당된 네트워크가 없습니다.
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>네트워크 프로필</TableCell>
                        <TableCell>IP 주소</TableCell>
                        <TableCell>MAC 주소</TableCell>
                        <TableCell>호스트명</TableCell>
                        <TableCell align="center">작업</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {vmNetworks.map((network, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Chip
                              label={network.network_profile}
                              color="primary"
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {network.actual_ip_address || network.ip_address || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {network.mac_address || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {network.hostname || 'N/A'}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              color="error"
                              onClick={() => handleUnassignNetwork(network.network_profile)}
                              title="네트워크 해제"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* 포트 포워딩 섹션 (NAT 네트워크인 경우에만) */}
              {(() => {
                // VM이 NAT 네트워크에 연결되어 있는지 확인
                const isNATNetwork = vmNetworks.some(vmNetwork => {
                  const networkInfo = availableNetworks.find(
                    net => net.name === vmNetwork.network_profile
                  );
                  return networkInfo && networkInfo.network_type === 'nat';
                });

                return (
                  <PortForwardingSection
                    vmName={vmName}
                    isNATNetwork={isNATNetwork}
                    vmActive={Boolean(vmStatus?.active)}
                  />
                );
              })()}
            </Box>
          )}

          {/* 디스크 관리 탭 */}
          {activeTab === (vmInfo?.gpu ? 4 : 3) && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">
                  디스크 관리
                </Typography>
                <Button 
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleMountDialogOpen}
                >
                  디스크 마운트
                </Button>
              </Box>
              
              {volumesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>장치</TableCell>
                        <TableCell>볼륨명</TableCell>
                        <TableCell>디스크 형식</TableCell>
                        <TableCell>버스 타입</TableCell>
                        <TableCell>읽기 전용</TableCell>
                        <TableCell align="center">작업</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {volumes.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography variant="body1" sx={{ py: 2 }}>
                              연결된 디스크가 없습니다.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        volumes.map((disk) => (
                          <TableRow key={disk.target_dev}>
                            <TableCell>
                              {disk.isRoot ? (
                                <Tooltip title="루트 디스크">
                                  <Chip 
                                    label={disk.target_dev} 
                                    color="primary" 
                                    size="small"
                                  />
                                </Tooltip>
                              ) : (
                                disk.target_dev
                              )}
                            </TableCell>
                            <TableCell>{disk.volume_name}</TableCell>
                            <TableCell>
                              <Chip 
                                label={disk.disk_format} 
                                size="small" 
                                variant="outlined"
                                color={disk.disk_format === "raw" ? "warning" : "default"}
                              />
                            </TableCell>
                            <TableCell>{disk.bus_type}</TableCell>
                            <TableCell>{disk.read_only ? '예' : '아니오'}</TableCell>
                            <TableCell align="center">
                              <IconButton 
                                color="error"
                                onClick={() => handleUnmountDisk(disk.target_dev)}
                                disabled={disk.target_dev === 'vda'} // 루트 디스크(vda)는 언마운트 비활성화
                                title={disk.target_dev === 'vda' ? "루트 디스크는 언마운트할 수 없습니다" : "디스크 언마운트"}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Cloud-init 관리 탭 */}
          {activeTab === (vmInfo?.gpu ? 5 : 4) && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">
                  Cloud-init 관리
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleCloudInitDialogOpen}
                  disabled={vmStatus?.active}
                >
                  Cloud-init ISO 변경
                </Button>
              </Box>

              {vmStatus?.active && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  VM이 실행 중입니다. Cloud-init ISO를 변경하려면 먼저 VM을 중지하세요.
                </Alert>
              )}

              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    현재 Cloud-init 설정
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2">ISO 파일</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {cloudInitIso ? cloudInitIso.name : '없음'}
                      </Typography>
                    </Grid>
                    {cloudInitIso && (
                      <>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2">파일 크기</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {cloudInitIso.size ? `${(cloudInitIso.size / 1024 / 1024).toFixed(2)} MB` : 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2">생성 시간</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {cloudInitIso.created_at ? new Date(cloudInitIso.created_at).toLocaleString() : 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2">파일 경로</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {cloudInitIso.path || 'N/A'}
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>

                  {!cloudInitIso && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        이 VM에는 Cloud-init ISO가 연결되어 있지 않습니다.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Cloud-init ISO를 연결하면 VM 초기화 설정을 사용할 수 있습니다.
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}
        </Box>
      </Paper>
      
      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>VM 삭제 확인</DialogTitle>
        <DialogContent>
          <DialogContentText>
            정말 VM "{vmName}"을(를) 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
          </DialogContentText>
          
          {/* 스토리지 삭제 옵션 */}
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              스토리지 삭제 옵션:
            </Typography>
            <Button
              variant={removeStorage ? "contained" : "outlined"}
              color="error"
              onClick={() => setRemoveStorage(true)}
              sx={{ mr: 1 }}
            >
              스토리지 함께 삭제
            </Button>
            <Button
              variant={!removeStorage ? "contained" : "outlined"}
              onClick={() => setRemoveStorage(false)}
            >
              VM만 삭제
            </Button>
          </Box>
          
          {/* Cloud-init ISO 삭제 옵션 (있는 경우에만 표시) */}
          {cloudInitIso && (
            <Box sx={{ mt: 3, mb: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Cloud-init ISO 파일:
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={removeCloudInitIso}
                    onChange={(e) => setRemoveCloudInitIso(e.target.checked)}
                  />
                }
                label={`Cloud-init ISO 파일 함께 삭제 (${cloudInitIso.name})`}
              />
              
              {/* 다른 VM에서 사용 중인 경우 경고 메시지 */}
              {isoInUseInfo && isoInUseInfo.in_use && isoInUseInfo.using_vms.length > 1 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  이 Cloud-init ISO는 다른 VM에서도 사용 중입니다:
                  {isoInUseInfo.using_vms
                    .filter(vm => vm.name !== vmName)
                    .map(vm => vm.name)
                    .join(', ')}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>취소</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            삭제
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 디스크 마운트 다이얼로그 */}
      <Dialog open={mountDialogOpen} onClose={handleMountDialogClose}>
        <DialogTitle>디스크 볼륨 마운트</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            VM에 디스크 볼륨을 마운트합니다. 볼륨을 선택하고 필요한 설정을 조정하세요.
          </DialogContentText>
          
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>디스크 볼륨</InputLabel>
            <Select
              name="volume_name"
              value={mountRequest.volume_name}
              onChange={handleMountInputChange}
              label="디스크 볼륨"
            >
              {availableVolumes.map((volume) => (
                <MenuItem key={volume.name} value={volume.name}>
                  {volume.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            margin="normal"
            label="대상 장치"
            name="target_dev"
            value={mountRequest.target_dev}
            onChange={handleMountInputChange}
            helperText="비워두면 자동으로 할당됩니다 (예: vdb, vdc 등)"
            fullWidth
          />
          
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>버스 타입</InputLabel>
            <Select
              name="bus_type"
              value={mountRequest.bus_type}
              onChange={handleMountInputChange}
              label="버스 타입"
            >
              <MenuItem value="virtio">virtio</MenuItem>
              <MenuItem value="ide">ide</MenuItem>
              <MenuItem value="scsi">scsi</MenuItem>
            </Select>
          </FormControl>
          
          <FormControlLabel
            control={
              <Checkbox
                name="read_only"
                checked={mountRequest.read_only}
                onChange={handleMountInputChange}
              />
            }
            label="읽기 전용"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMountDialogClose}>취소</Button>
          <Button onClick={handleMountDisk} variant="contained" color="primary">
            마운트
          </Button>
        </DialogActions>
      </Dialog>

      {/* 네트워크 할당 다이얼로그 */}
      <Dialog open={networkDialogOpen} onClose={() => setNetworkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>네트워크 할당</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            VM에 네트워크를 할당합니다. 네트워크 프로필을 선택하고 필요시 IP 주소를 지정하세요.
          </DialogContentText>
          
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>네트워크 프로필</InputLabel>
            <Select
              value={networkRequest.network_profile}
              onChange={(e) => setNetworkRequest({ ...networkRequest, network_profile: e.target.value })}
              label="네트워크 프로필"
            >
              {availableNetworks.map((network) => (
                <MenuItem key={network.name} value={network.name}>
                  {network.name} ({network.cidr})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            fullWidth
            label="IP 주소 (선택사항)"
            value={networkRequest.ip_address}
            onChange={(e) => setNetworkRequest({ ...networkRequest, ip_address: e.target.value })}
            helperText="비워두면 자동으로 할당됩니다"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNetworkDialogOpen(false)}>취소</Button>
          <Button 
            onClick={handleAssignNetwork} 
            variant="contained" 
            color="primary"
            disabled={!networkRequest.network_profile}
          >
            할당
          </Button>
        </DialogActions>
      </Dialog>

      {/* VM 재시작 확인 다이얼로그 */}
      <Dialog open={networkRestartDialogOpen} onClose={() => setNetworkRestartDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>VM 재시작 필요</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            VM이 현재 실행 중입니다. 네트워크 변경을 적용하려면 VM을 중단하고 재시작해야 합니다.
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>주의:</strong> VM이 일시적으로 중단됩니다. 실행 중인 작업이 있다면 먼저 저장하세요.
          </Alert>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              진행 과정:
            </Typography>
            <Typography variant="body2" component="div" sx={{ ml: 1, mt: 1 }}>
              1. VM 중지<br/>
              2. 네트워크 설정 변경<br/>
              3. VM 재시작
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNetworkRestartDialogOpen(false)}>취소</Button>
          <Button 
            onClick={performNetworkAssignment} 
            variant="contained" 
            color="warning"
            disabled={networksLoading}
          >
            {networksLoading ? '처리 중...' : '계속 진행'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cloud-init ISO 변경 다이얼로그 */}
      <Dialog open={cloudInitDialogOpen} onClose={() => setCloudInitDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cloud-init ISO 변경</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            VM에 연결할 Cloud-init ISO를 선택하거나 제거할 수 있습니다.
          </DialogContentText>
          
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Cloud-init ISO</InputLabel>
            <Select
              value={selectedCloudInitIso}
              onChange={(e) => setSelectedCloudInitIso(e.target.value)}
              label="Cloud-init ISO"
            >
              <MenuItem value="">
                <em>ISO 제거 (연결 해제)</em>
              </MenuItem>
              {availableCloudInitIsos.map((iso) => {
                const usage = isoUsageInfo[iso.name];
                const usageText = usage && usage.in_use 
                  ? ` - 사용중: ${usage.using_vms.map(vm => vm.name).join(', ')}`
                  : '';
                
                return (
                  <MenuItem key={iso.name} value={iso.name}>
                    {iso.name} ({iso.hostname || 'Unknown hostname'}){usageText}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {selectedCloudInitIso && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                선택된 ISO 정보:
              </Typography>
              {availableCloudInitIsos
                .filter(iso => iso.name === selectedCloudInitIso)
                .map(iso => (
                  <Box key={iso.name}>
                    <Typography variant="body2" color="text.secondary">
                      호스트명: {iso.hostname || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      크기: {iso.size ? `${(iso.size / 1024 / 1024).toFixed(2)} MB` : 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      생성일: {iso.created_time ? new Date(iso.created_time * 1000).toLocaleString() : 'N/A'}
                    </Typography>
                  </Box>
                ))
              }
            </Box>
          )}

          {!selectedCloudInitIso && (
            <Alert severity="info" sx={{ mt: 2 }}>
              ISO를 제거하면 Cloud-init 기능을 사용할 수 없습니다.
            </Alert>
          )}

          {cloudInitLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloudInitDialogOpen(false)}>취소</Button>
          <Button 
            onClick={handleUpdateCloudInitIso} 
            variant="contained" 
            color="primary"
            disabled={cloudInitLoading}
          >
            {cloudInitLoading ? '변경 중...' : '변경'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 알림 스낵바 */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VMDetailPage; 